import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { PlotsTable } from '@/components/plots/plots-table'
import { Pagination } from '@/components/members/pagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePlotsList } from '@/hooks/use-plots'
import type { PlotListItem } from '@riverpark/types'

export interface PlotsListPageProps {
  plotsBasePath: string
  membersBasePath: string
  canManage?: boolean
}

export function PlotsListPage({
  plotsBasePath,
  membersBasePath,
  canManage = true,
}: PlotsListPageProps) {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<PlotListItem['status'] | 'all'>('all')
  const [section, setSection] = useState('all')

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [status, section])

  const limit = 20
  const { data, isLoading, error } = usePlotsList({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    section: section === 'all' ? undefined : section,
  })

  const plots = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit, total: 0, totalPages: 1 }

  return (
    <PageShell>
      <PageHeader
        label="Management"
        title="Plots & Map"
        description="Plot registry, ownership, and estate map."
        action={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Link
                to={`${plotsBasePath}/import`}
                className="inline-flex h-10 items-center justify-center rounded border border-border bg-transparent px-5 font-sans text-[13px] font-medium tracking-wide text-ink transition-colors hover:border-ink"
              >
                Import plots
              </Link>
              <Link to={`${plotsBasePath}/new`}>
                <Button type="button">Add plot</Button>
              </Link>
            </div>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1">
          <Input
            label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Plot number, street, section…"
          />
        </div>
        <div>
          <label className="mb-1.5 block font-sans text-sm font-medium text-ink">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as PlotListItem['status'] | 'all')}
            className="flex h-10 rounded border border-input bg-card px-3 font-sans text-sm text-ink"
          >
            <option value="all">All statuses</option>
            <option value="occupied">Occupied</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block font-sans text-sm font-medium text-ink">Section</label>
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            className="flex h-10 rounded border border-input bg-card px-3 font-sans text-sm text-ink"
          >
            <option value="all">All sections</option>
            <option value="West">West</option>
            <option value="Central">Central</option>
            <option value="Central-East">Central-East</option>
            <option value="East">East</option>
          </select>
        </div>
      </div>

      {error && (
        <p className="mb-4 font-sans text-sm text-destructive" role="alert">
          Could not load plots.
        </p>
      )}

      <PlotsTable
        plots={plots}
        isLoading={isLoading}
        plotsBasePath={plotsBasePath}
        membersBasePath={membersBasePath}
        emptyMessage={
          canManage
            ? 'No plots found. Import the plot register or add a plot manually.'
            : 'No plots found.'
        }
      />

      <div className="mt-4">
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          className="rounded-b-card border border-t-0 border-border-2 bg-card"
        />
      </div>
    </PageShell>
  )
}
